import React, { useEffect, useState } from 'react';
import { useDrop } from 'react-dnd';
import { Object3D } from 'three';
import PropertyBase, { IBasePropertyProps } from './PropertyBase';
import Button from '../layout/Button';
import CameraPicker from '../CameraPicker';
import Engine from '../../classes/Engine';
import ModalManager from '../layout/ModalManager';
import { CustomEventTypes, DndItemTypes, ObjectClasses } from '../../globals';
import SceneObjectUtils from '../../classes/utilities/SceneObjectUtils';
import useForceUpdate from '../reactUtils/useForceUpdate';
import useEventListener from '../reactUtils/useEventListener';
import AnalyticsManager from '../../classes/AnalyticsManager';

export default function CameraProperty(props: IBasePropertyProps)
{
	const forceUpdate = useForceUpdate();

	useEventListener(CustomEventTypes.objectNameChanged, () => forceUpdate(), document);
	useEventListener(CustomEventTypes.hierarchyChangedEvent, () => forceUpdate(), document);

	const [selectionRoot, setSelectionRoot] = useState<Object3D | null>(null);

	useEffect(() => 
	{
		setSelectionRoot(Engine.getInstance().getSelectionRoot());
	}, []);

	useEffect(() =>
	{
		forceUpdate();
	}, [selectionRoot]);

	const [{ canDrop }, drop] = useDrop({
		accept: [
			DndItemTypes.SCENE_HIERARCHY_TREE_ITEM,
		],
		collect: monitor => ({
			hovered: monitor.isOver({ shallow: true }),
			canDrop: monitor.canDrop(),
		}),
		canDrop: (item: any) =>
		{
			return item.selected[0].props.objectClass === ObjectClasses.Camera;
		},
		drop: (item: any, monitor) =>
		{
			if (!monitor.didDrop())
			{
				cameraSelected(item.selected[0].props.nodeId);
			}
		},
	});

	const cameraSelected = (cameraId: string, managedEventName = "") => 
	{
		AnalyticsManager.managedEvent('propertyChanged', 'propertyId', managedEventName);

		if (props.onCommitCallback)
		{
			props.onCommitCallback(cameraId);
		}
		else if (props.onChangeCallback)
		{
			props.onChangeCallback(cameraId);
		}
	};

	const sceneObject = selectionRoot && SceneObjectUtils.findObjectByGuid(selectionRoot, props.value);
	const faceValue = sceneObject?.name || "Select Camera";
	const dataName = (props["data-name"] ? props["data-name"] + "-" : "") + "-camera-property";

	return drop(
		<div className={"objectPropertyWrapper"}>
			<PropertyBase {...props}
				data-name={dataName + "-root"}
				highlight={canDrop}
			>
				<Button
					silent={true}
					data-name={dataName + "-button"}
					label={faceValue}
					onClick={(e) =>
					{
						e.persist();
						ModalManager.addModal({
							content:
								<CameraPicker
									data-name={dataName + "-picker"}
									value={props.value}
									cameraSelectedCallback={(cameraId) =>
									{
										cameraSelected(cameraId, e.target["dataset"]?.name);
									}}
								/>
						});
					}}
				/>
			</PropertyBase>
		</div>
	);
}